import { useRoomContext } from "@livekit/components-react";
import { Room, RoomEvent } from "livekit-client";
import { useEffect, useRef } from "react";
import { LiveKitActionMessageType, sendLiveKitMessage } from "./AssessmentUtils";

enum LiveKitTimerMessageType {
  TimerStarted = "timer_started",
  TimerEnded = "timer_ended",
  TimerUpdated = "timer_updated",
  CleanupStatusUpdate = "cleanup_status_update",
  InitializationError = "initialization_error",
  QuestionIndexUpdate = "question_index_update",
}

interface LiveKitTimerMessage {
  type: LiveKitTimerMessageType;
  assessmentId: string;
  assessmentRemainingTime?: string;
  isCleanupInProgress?: boolean;
  error?: string;
  questionIndex?: number;
}

interface AssessmentMessageHandlerProps {
  assessmentId: string;
  setIsCleanupInProgress: (inProgress: boolean) => void;
  onRoomReady: (room: Room) => void;
  assessmentIsRunning: boolean;
  onInitializationErrorCallback: () => void;
  onComplete: () => void;
  onTimerUpdated: (time: string) => void;
  onTimerStarted: (time: string) => void;
  onQuestionIndexUpdate: (index: number) => void;
}

function AssessmentMessageHandler({
  assessmentId,
  setIsCleanupInProgress,
  onRoomReady,
  assessmentIsRunning,
  onInitializationErrorCallback,
  onComplete,
  onTimerUpdated,
  onTimerStarted,
  onQuestionIndexUpdate,
}: AssessmentMessageHandlerProps) {
  const room = useRoomContext();
  const messageRequestSent = useRef(false);
  const hasSetupEventListeners = useRef(false);

  useEffect(() => {
    if (room) {
      onRoomReady(room);
    }
  }, [room, onRoomReady]);

  useEffect(() => {
    if (!room || hasSetupEventListeners.current) {
      return;
    }

    hasSetupEventListeners.current = true;

    const handleData = (payload: Uint8Array) => {
      try {
        const message = JSON.parse(new TextDecoder().decode(payload)) as LiveKitTimerMessage;

        if (message.assessmentId !== assessmentId) {
          return;
        }

        switch (message.type) {
          case LiveKitTimerMessageType.TimerEnded:
            onComplete();
            break;
          case LiveKitTimerMessageType.TimerUpdated:
            onTimerUpdated(message.assessmentRemainingTime);
            break;
          case LiveKitTimerMessageType.TimerStarted:
            onTimerStarted(message.assessmentRemainingTime);
            break;
          case LiveKitTimerMessageType.CleanupStatusUpdate:
            if (message.isCleanupInProgress !== undefined) {
              setIsCleanupInProgress(message.isCleanupInProgress);
            }
            break;
          case LiveKitTimerMessageType.InitializationError:
            if (message.error) {
              onInitializationErrorCallback();
            }
            break;
          case LiveKitTimerMessageType.QuestionIndexUpdate:
            if (message.questionIndex !== undefined) {
              onQuestionIndexUpdate(message.questionIndex);
            }
            break;
          default:
            console.error("Unknown message type:", message.type);
            break;
        }
      } catch (error) {
        console.error("Error parsing message from agent:", error);
      }
    };

    room.on(RoomEvent.DataReceived, handleData);

    room.on(RoomEvent.ConnectionStateChanged, (state) => {
      if (state === "connected") {
        sendLiveKitMessage(LiveKitActionMessageType.RequestAssessmentTime, room, assessmentId);
        sendLiveKitMessage(LiveKitActionMessageType.RequestAssessmentQuestionIndex, room, assessmentId);
        messageRequestSent.current = true;
      }
    });

    return () => {
      if (room) {
        room.off(RoomEvent.DataReceived, handleData);
        room.off(RoomEvent.ConnectionStateChanged, () => {});
        hasSetupEventListeners.current = false;
      }
    };
  }, [room, assessmentId, assessmentIsRunning]);

  return null;
}

export default AssessmentMessageHandler;
